export interface OnSiteGuestSearchable {
  guestName: string;
  pitchCode: string | null;
  keyNumber: string | null;
  transponderId: string | null;
  licensePlate: string | null;
}

function normalizeSearchTerm(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "");
}

function normalizeAccessKey(value: string): string | null {
  const normalized = value.trim().replace(/[^A-Za-z0-9]+/g, "").toUpperCase();
  return normalized || null;
}

function normalizeLicensePlate(value: string): string | null {
  const normalized = value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
  return normalized.length >= 2 ? normalized : null;
}

/** Match display name "Nachname, Vorname" against "Vorname Nachname" and partial queries. */
export function guestNameMatchesQuery(query: string, guestName: string): boolean {
  const q = normalizeSearchTerm(query);
  if (!q) return false;
  const name = normalizeSearchTerm(guestName);
  if (name.includes(q)) return true;

  const tokens = query
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(normalizeSearchTerm);
  if (tokens.length < 2) return false;

  const reversedComma = tokens.slice().reverse().join(", ");
  if (name.includes(reversedComma)) return true;

  return tokens.every((token) => name.includes(token));
}

export function filterOnSiteGuests<T extends OnSiteGuestSearchable>(
  guests: T[],
  rawQuery: string,
): T[] {
  const q = rawQuery.trim();
  if (!q) return [];

  const lower = q.toLowerCase();
  const accessNorm = normalizeAccessKey(q);
  const plateNorm = normalizeLicensePlate(q);

  return guests.filter((guest) => {
    if (guest.pitchCode && guest.pitchCode.toLowerCase() === lower) return true;
    if (guest.pitchCode && guest.pitchCode.toLowerCase().includes(lower)) return true;
    if (guestNameMatchesQuery(q, guest.guestName)) return true;
    if (plateNorm && guest.licensePlate) {
      const guestPlate = normalizeLicensePlate(guest.licensePlate);
      if (guestPlate && guestPlate.includes(plateNorm)) return true;
    }
    if (accessNorm) {
      const keyNorm = guest.keyNumber ? normalizeAccessKey(guest.keyNumber) : null;
      const transNorm = guest.transponderId ? normalizeAccessKey(guest.transponderId) : null;
      if (keyNorm === accessNorm || transNorm === accessNorm) return true;
    }
    return false;
  });
}

/** Exact pitch-code matches among on-site guests (avoids key/transponder collisions). */
export function findExactPitchCodeMatches<T extends OnSiteGuestSearchable>(
  guests: T[],
  rawQuery: string,
): T[] {
  const code = rawQuery.trim().toLowerCase();
  if (!code) return [];
  return guests.filter((guest) => guest.pitchCode?.toLowerCase() === code);
}
